import {UI} from "../../../../../../stem-core/src/ui/UIBase";

import {BasePanelPage} from "../../BasePanelPage";
import {Messages} from "../../../../../Messages";
import {subscribeFlowState} from "../../../../inline-subscription/SubscribeFlowState";
import {iFrameUserDataService} from "../../../../../services/IFrameUserDataService";
import {Link} from "../../../../../../stem-core/src/ui/UIPrimitives";
import {INLINE_EDIT_SUBSCRIPTION_URLS} from "../../../../PaywallConstants";
import {CreditCardIcon} from "../../../../../ui/CreditCardIcon";
import {SubscriptionApplyPolicy} from "../../../../../../client/state/SubscriptionStore";
import {LabeledCheckbox} from "../../../../../ui/Checkbox";
import {MerchantCheckbox} from "../../../../../widget/ui/MerchantCheckbox";
import {MerchantButton} from "../../../../../common/MerchantButton";
import {Router} from "../../../../../../stem-core/src/ui/Router";
import {styleRule, styleRuleInherit} from "../../../../../../stem-core/src/decorators/Style";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {SubscribeReviewOrderPanelStyle} from "../SubscribeReviewOrderPanel";
import {addressController} from "../../../../inline-subscription/AddressController";
import {subscribePlanController} from "../../../../inline-subscription/SubscribePlanController";
import {MessageElement} from "../../../../../widget/ui/MessageElement";
import {UsingBlinkDefaultPaymentMethod} from "../../../../../common/UsingBlinkDefaultPaymentMethod";
import {autoredraw} from "../../../../../../stem-core/src/decorators/AutoRedraw";
import {PaymentMethodStore} from "../../../../../State";
import {iFrameMerchantService} from "../../../../../services/IframeMerchantService";


class EditSubscriptionPanelStyle extends SubscribeReviewOrderPanelStyle {
    @styleRuleInherit
    reviewItem = {
        ":first-child": {
            marginTop: 0,
        }
    };

    @styleRuleInherit
    reviewItemTitleContainer = {
        marginBottom: 8,
    };

    @styleRuleInherit
    reviewItemTitle = {
        marginBottom: "",
    };

    @styleRule
    checkbox = {
        color: "inherit",
        fontSize: "inherit",
        marginBottom: 8,
    };

    @styleRule
    cardIcon = {
        display: "inline-block",
        marginRight: 12,
    };

    @styleRule
    cardDetails = {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    };
}

@autoredraw(PaymentMethodStore)
@registerStyle(EditSubscriptionPanelStyle)
export class EditSubscriptionPanel extends BasePanelPage {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            title: Messages.manageSubscription,
        };
    }

    isPrimaryButtonDisabled() {
        const {selectedSubscriptionOffer, shippingAddress, preferredPaymentMethod, discountCode} = subscribeFlowState.getSubscriptionInfo();
        const subscription = iFrameUserDataService.getActiveSubscription();

        return subscription.getOffer() === selectedSubscriptionOffer &&
            subscription.getShippingAddress() === shippingAddress &&
            subscription.getPaymentMethod() === preferredPaymentMethod &&
            (subscription.getDiscount()?.getCode() || null) === discountCode;
    }

    renderCheckboxAndPriceInfo() {
        const {selectedSubscriptionOffer, applyPolicy} = subscribeFlowState.getSubscriptionInfo();
        const instantPayInfo = subscribeFlowState.getDiscountInfoOnInstantChangePolicy(selectedSubscriptionOffer);
        const nextCyclePayInfo = subscribeFlowState.getDiscountInfo(selectedSubscriptionOffer);
        const activeSubscription = iFrameUserDataService.getActiveSubscription();

        if (!instantPayInfo || (selectedSubscriptionOffer === activeSubscription.getOffer() &&
            selectedSubscriptionOffer.formatPrice(nextCyclePayInfo.finalPrice).toString() === activeSubscription.getPrice().toString())) {
            return null;
        }

        const {styleSheet} = this;
        const paidUntil = activeSubscription.getPaidUntil();
        const nextBillingDate = activeSubscription.getNextBillingDate();

        const paymentInfoMessageArgs = [
            selectedSubscriptionOffer.formatPrice(nextCyclePayInfo.finalPrice),
            selectedSubscriptionOffer.getCycleDuration(),
            nextBillingDate.format("MMM DD, YYYY")
        ];
        const updatePaymentInfo = applyPolicy === SubscriptionApplyPolicy.NEXT_BILLING_CYCLE ? [
                Messages.updateAtEndCyclePaymentInfo(...paymentInfoMessageArgs),
                selectedSubscriptionOffer !== activeSubscription.getOffer() ? Messages.updateAtEndCycleChangeOffer : ""].join("")
            : Messages.updateInstantlyPaymentInfo(
                selectedSubscriptionOffer.formatPrice(instantPayInfo.finalPrice),
                ...paymentInfoMessageArgs);

        let checkboxElement = null;

        if (instantPayInfo.finalPrice !== nextCyclePayInfo.finalPrice) {
            checkboxElement = <LabeledCheckbox className={styleSheet.checkbox} testId="applyPolicyCheckbox"
                                               onChange={enabled => {
                                                   subscribePlanController.applyPolicy = enabled ? SubscriptionApplyPolicy.NEXT_BILLING_CYCLE :
                                                       SubscriptionApplyPolicy.INSTANTLY;
                                                   this.redraw();
                                               }}
                                               CheckboxClass={MerchantCheckbox} initialValue={applyPolicy === SubscriptionApplyPolicy.NEXT_BILLING_CYCLE}
                                               label={Messages.updateAtEndCycleCheckbox(paidUntil.format("MMM DD, YYYY"))}/>;
        }

        return [
            <div className={styleSheet.lineSeparator}/>,
            <div className={styleSheet.reviewItem} testId="paymentInfoSection">
                {checkboxElement}
                <MessageElement message={updatePaymentInfo}/>
            </div>
        ];
    }

    render() {
        const {styleSheet} = this;
        const {selectedSubscriptionOffer, shippingAddress, preferredPaymentMethod} = subscribeFlowState.getSubscriptionInfo();
        let discountInfo = subscribeFlowState.getDiscountInfo(selectedSubscriptionOffer);
        if (!discountInfo) {
            subscribeFlowState.updateEditPricesFromExistingSubscription(iFrameUserDataService.getActiveSubscription());
            discountInfo = subscribeFlowState.getDiscountInfo(selectedSubscriptionOffer);
            if (!discountInfo) {
                return null;
            }
        }
        const finalPrice = selectedSubscriptionOffer.formatPrice(discountInfo.finalPrice);
        const subscriptionPlanArgs = [selectedSubscriptionOffer.getName(), finalPrice, selectedSubscriptionOffer.getCycleDuration()];
        const subscriptionPlanText = selectedSubscriptionOffer === iFrameUserDataService.getActiveSubscription().getOffer() ?
            Messages.editSubscriptionPlanDescription(...subscriptionPlanArgs) :
            (discountInfo.finalPrice === discountInfo.discountedBeforeTaxesPrice ?
                Messages.editSubscriptionChangePlanDescription(...subscriptionPlanArgs) :
                Messages.editSubscriptionChangePlanWithShippingIncludedDescription(...subscriptionPlanArgs));

        return [
            <div className={styleSheet.reviewItem} testId="subscriptionPlanSection">
                <div className={styleSheet.reviewItemTitleContainer}>
                    <div className={styleSheet.reviewItemTitle}>{Messages.subscriptionPlan}</div>
                    <Link href={INLINE_EDIT_SUBSCRIPTION_URLS.changePlan} value={Messages.change} className={styleSheet.link}
                          testId="changeButton"/>
                </div>
                <div className={""}>
                    <MessageElement message={subscriptionPlanText} />
                </div>
            </div>,
            selectedSubscriptionOffer.shouldCollectAddress() ? [
                <div className={styleSheet.lineSeparator}/>,
                <div className={styleSheet.reviewItem} testId="addressSection">
                    <div className={styleSheet.reviewItemTitleContainer}>
                        <div className={styleSheet.reviewItemTitle}>{Messages.address}</div>
                        <Link href={INLINE_EDIT_SUBSCRIPTION_URLS.address} value={Messages.edit}
                              className={styleSheet.link} testId="editButton"/>
                    </div>
                    <div>
                        <div className={styleSheet.nameOnAddress}>{shippingAddress?.getRecipientName()}</div>
                        <div className={styleSheet.addressDetails}>{shippingAddress}</div>
                    </div>
                </div>
            ] : null,
            <div className={styleSheet.lineSeparator}/>,
            <div className={styleSheet.reviewItem} testId="paymentMethodSection">
                <div className={styleSheet.reviewItemTitleContainer}>
                    <div className={styleSheet.reviewItemTitle}>{Messages.paymentMethod}</div>
                    <Link href={INLINE_EDIT_SUBSCRIPTION_URLS.paymentDetails}
                          value={preferredPaymentMethod ? Messages.edit : Messages.addNewCard}
                          className={styleSheet.link} testId="editButton"/>
                </div>
                {
                    preferredPaymentMethod ?
                    <div className={styleSheet.cardDetails}>
                        <CreditCardIcon className={styleSheet.cardIcon} brand={preferredPaymentMethod.getBrand()}/>
                        <MessageElement message={Messages.usingCardSubscribe(
                            preferredPaymentMethod.getBrand(), preferredPaymentMethod.getLastDigits())}/>
                    </div> : (
                        iFrameUserDataService.hasPrimaryPaymentMethod() && !iFrameMerchantService.isCDSPaymentMethod()?
                            <UsingBlinkDefaultPaymentMethod /> :
                            <div className={styleSheet.cardDetails}>
                                <div>{Messages.noPaymentMethodForThisSubscription}</div>
                            </div>
                    )
                }
            </div>,
            this.renderCheckboxAndPriceInfo(),
            <MerchantButton disabled={this.isPrimaryButtonDisabled()} className={styleSheet.button}
                            onClick={() => subscribeFlowState.changeSubscription()}>
                {Messages.saveChanges}
            </MerchantButton>,
            this.isPrimaryButtonDisabled() ?
            <div className={styleSheet.center}>
                <div onClick={() => Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.cancel)}
                     className={styleSheet.secondaryButton}>{Messages.cancelSubscription}</div>
            </div> : null
        ];
    }

    onMount() {
        super.onMount();
        this.attachListener(addressController, () => this.redraw());
        this.attachListener(subscribeFlowState, "requestsFinished", () => {
            this.redraw();
        });
    }
}
